import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { FloorTypename } from "shared/types/Project"

import { useUpdateAttachmentLinks } from "entities/attachments/api/useUpdateAttachmentLinks"
import { useUpdateFloor } from "entities/floors/api/useUpdateFloor"
import { useFloorFragment } from "entities/floors/model/useFloorFragment"
import { useFloor } from "features/floor-management/model/useFloor"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { useFloorForm } from "widgets/floor/floor-form/model/useFloorForm"

import { EditFloorOverlayModel } from "../EditFloorOverlay"
import { EditFloorOverlayConnectorProps } from "../EditFloorOverlayConnector"

export function useEditFloorOverlayModel({
  payload: { projectID, floorID, defaultTab }
}: EditFloorOverlayConnectorProps): EditFloorOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const [updateFloor] = useUpdateFloor()
  const updateAttachmentLinks = useUpdateAttachmentLinks()
  const floor = useFloorFragment(floorID)
  const floorModel = useFloor({ floorID, projectID })

  const submitFloor = useEvent(async (floorUpdates: FloorFormData) => {
    floorModel.clarificationRequest?.handlers.onSubmitContent?.(
      floorUpdates.clarificationRequest?.content
    )
    const results = await Promise.all([
      updateFloor({
        projectID,
        displayPosition: floorUpdates.displayPosition,
        floor: { ...floorUpdates.floor, id: floorID }
      }),
      floorUpdates.attachments
        ? updateAttachmentLinks({
            id: floorID,
            resourceType: FloorTypename,
            attachmentLinks: floorUpdates.attachments
          })
        : Promise.resolve({ data: {}, errors: undefined })
    ])
    const errors = results.flatMap(r => r?.errors ?? [])
    return { data: {}, errors: errors.length ? errors : undefined }
  })

  const { handleSubmit, isSubmitting } = useFormSubmit<FloorFormData>({
    onSubmit: submitFloor,
    onSuccess: closeOverlay
  })

  const floorFormModel = useFloorForm({
    projectID,
    parentID: projectID,
    onSubmit: handleSubmit,
    formOptions: { hideProject: true },
    floor,
    model: floorModel
  })

  return {
    floorFormModel,
    isLoading: isSubmitting,
    isSubmitting,
    defaultTab,
    onCancel: closeOverlay
  }
}
